import { TradeData } from './types';
import { parse, isValid, formatISO } from 'date-fns';

// More comprehensive list of possible column name variations
const COLUMN_MAPPINGS: { [key in keyof Partial<TradeData>]: string[] } = {
  // Primary fields
  timestamp: ['Date', 'Timestamp', 'Time', 'Execution Time', 'Trade Date', 'Trade_Date', 'Transaction Date', 'Entry Date', 'Date/Time'],
  // Special handlers for separate date & time columns
  date: ['Date', 'Trade Date', 'Trade_Date', 'Transaction Date', 'Entry Date', 'Order Date'],
  time: ['Time', 'Trade Time', 'Execution Time', 'Entry Time', 'Order Time'],
  symbol: ['Symbol', 'Ticker', 'Security', 'Stock', 'Instrument', 'Asset'],
  action: ['Action', 'Side', 'Type', 'Buy/Sell', 'Transaction Type', 'Order Type', 'Direction', 'Buy/Sell'],
  quantity: ['Quantity', 'Qty', 'Shares', 'Amount', 'Size', 'Volume', 'Position Size'],
  price: ['Price', 'Execution Price', 'Execution_Price', 'Avg Price', 'Cost Basis', 'Entry Price', 'Fill Price'], 
  status: ['Status', 'Order Status', 'Fill Status', 'State'],
  // Map profitLoss if available, otherwise it needs calculation
  profitLoss: ['P/L', 'Profit/Loss', 'Profit', 'Gain/Loss', 'Realized PnL', 'Realized_PnL', 'PnL', 'Returns'],
  // Map ID if available
  id: ['Trade ID', 'Order ID', 'Execution ID', 'ID', 'Transaction ID'],
};

// Potential date formats to try parsing
const DATE_FORMATS = [
  'yyyy-MM-dd HH:mm:ss',
  'yyyy-MM-dd HH:mm',
  'MM/dd/yyyy HH:mm:ss',
  'MM/dd/yyyy HH:mm',
  'MM/dd/yy HH:mm',
  'yyyy-MM-dd',
  'MM/dd/yyyy',
  'MM/dd/yy',
  'yyyyMMdd',
  // Add ISO format with timezone handling
  "yyyy-MM-dd'T'HH:mm:ss.SSSxxx", // e.g., 2023-10-27T10:00:00.000-04:00
  "yyyy-MM-dd'T'HH:mm:ssxxx",   // e.g., 2023-10-27T10:00:00-04:00
  "yyyy-MM-dd'T'HH:mm:ss'Z'",   // UTC Zulu time
  "yyyy-MM-dd'T'HH:mm:ss.SSS'Z'", // UTC Zulu time with milliseconds
];

const parseDate = (dateString: string): string | null => {
    if (!dateString) return null;
    for (const fmt of DATE_FORMATS) {
        try {
            const parsedDate = parse(dateString, fmt, new Date());
            if (isValid(parsedDate)) {
                return formatISO(parsedDate); // Standardize to ISO string
            }
        } catch (e) {/* ignore and try next format */}
    }
    // If it looks like a timestamp number
    if (/^\d+$/.test(dateString)) {
        try {
            const timestampNum = parseInt(dateString, 10);
            // Basic check for plausible timestamp range (e.g., > year 2000)
            if (timestampNum > 946684800000) {
                 const parsedDate = new Date(timestampNum);
                 if(isValid(parsedDate)) return formatISO(parsedDate);
            }
        } catch (e) {/* ignore */}
    }

    console.warn(`Could not parse date: ${dateString}`);
    return null; // Return null if no format matches
};

const normalizeAction = (actionString: string): 'buy' | 'sell' | null => {
  const lowerAction = actionString?.toLowerCase().trim();
  if (lowerAction === 'buy' || lowerAction === 'long' || lowerAction === 'b') {
    return 'buy';
  }
  if (lowerAction === 'sell' || lowerAction === 'short' || lowerAction === 's') {
    return 'sell';
  }
  console.warn(`Could not normalize action: ${actionString}`);
  return null;
};

export const mapCsvToTrades = (csvData: any[]): TradeData[] => {
  if (!csvData || csvData.length === 0) {
    return [];
  }

  const trades: TradeData[] = [];
  const headers = Object.keys(csvData[0] || {});
  const mappedHeaders: { [key: string]: string } = {}; // Changed to allow custom keys

  // Print headers for debugging
  console.log("CSV Headers found:", headers);

  // Find the actual header names in the CSV corresponding to our TradeData fields
  for (const key in COLUMN_MAPPINGS) {
    const possibleNames = COLUMN_MAPPINGS[key as keyof TradeData]!;
    const foundHeader = headers.find(h => 
      possibleNames.some(pn => pn.toLowerCase() === h.toLowerCase().trim())
    );
    if (foundHeader) {
      mappedHeaders[key] = foundHeader;
    }
  }

  console.log("Mapped headers:", mappedHeaders);

  // Check for essential fields or combinations that could satisfy them
  let missingEssentials: string[] = [];
  
  // Timestamp check - either direct timestamp OR date+time combo
  if (!mappedHeaders.timestamp && !(mappedHeaders.date && mappedHeaders.time)) {
    missingEssentials.push('timestamp/date fields');
  }
  
  // Other essential checks
  if (!mappedHeaders.symbol) missingEssentials.push('symbol');
  if (!mappedHeaders.action) missingEssentials.push('action/side');
  if (!mappedHeaders.quantity) missingEssentials.push('quantity');
  if (!mappedHeaders.price) missingEssentials.push('price');

  if (missingEssentials.length > 0) {
    throw new Error(
      `CSV file is missing essential columns: ${missingEssentials.join(', ')}. ` +
      `Required columns include variations of Date (with optional Time), Symbol, Action/Side, Quantity, and Price.`
    );
  }

  // Process data rows
  const errorMessages: { [rowIndex: number]: string } = {};
  
  csvData.forEach((row, index) => {
    try {
      // Normalize essential fields
      const rowErrors: string[] = [];
      let timestamp: string | null = null;
      
      // Handle timestamp parsing - either direct or combined from date+time
      if (mappedHeaders.timestamp) {
        timestamp = parseDate(row[mappedHeaders.timestamp]);
        if (!timestamp) {
          rowErrors.push(`Invalid timestamp: ${row[mappedHeaders.timestamp]}`);
        }
      } else if (mappedHeaders.date) {
        // Try to combine date and time
        const dateValue = row[mappedHeaders.date];
        const timeValue = mappedHeaders.time ? row[mappedHeaders.time] || '00:00:00' : '00:00:00';
        
        const combinedDateTime = `${dateValue} ${timeValue}`;
        timestamp = parseDate(combinedDateTime);
        
        if (!timestamp) {
          rowErrors.push(`Could not parse date/time: ${combinedDateTime}`);
        }
      }
      
      // Handle action/side
      const action = normalizeAction(row[mappedHeaders.action]);
      if (!action) {
        rowErrors.push(`Invalid action/side: ${row[mappedHeaders.action]}`);
      }
      
      // Handle symbol
      const symbol = row[mappedHeaders.symbol]?.trim().toUpperCase();
      if (!symbol) {
        rowErrors.push('Missing symbol');
      }
      
      // Handle quantity with robust parsing
      let quantity = NaN;
      try {
        const qtyStr = row[mappedHeaders.quantity]?.toString().replace(/,/g, '').trim();
        quantity = qtyStr ? parseFloat(qtyStr) : NaN;
      } catch (e) {
        rowErrors.push(`Invalid quantity: ${row[mappedHeaders.quantity]}`);
      }
      
      if (isNaN(quantity) || quantity <= 0) {
        rowErrors.push(`Invalid quantity value: ${row[mappedHeaders.quantity]}`);
      }
      
      // Handle price with robust parsing
      let price = NaN;
      try {
        const priceStr = row[mappedHeaders.price]?.toString().replace(/[^0-9.-]+/g, '').trim();
        price = priceStr ? parseFloat(priceStr) : NaN;
      } catch (e) {
        rowErrors.push(`Invalid price: ${row[mappedHeaders.price]}`);
      }
      
      if (isNaN(price) || price <= 0) {
        rowErrors.push(`Invalid price value: ${row[mappedHeaders.price]}`);
      }
      
      // If any essential fields invalid, collect errors and skip row
      if (rowErrors.length > 0) {
        errorMessages[index] = `Row ${index + 1}: ${rowErrors.join('; ')}`;
        console.warn(`Skipping row ${index + 1}:`, errorMessages[index], row);
        return; // Skip this row
      }
      
      // Create valid trade record
      const trade: TradeData = {
        id: mappedHeaders.id ? row[mappedHeaders.id] : `csv_${index}_${Date.now()}`,
        timestamp: timestamp!,
        symbol, 
        action: action!,
        quantity,
        price,
        status: mappedHeaders.status ? 
          (row[mappedHeaders.status]?.toLowerCase() as any) || 'filled' : 
          'filled',
        profitLoss: undefined
      };
      
      // Handle profit/loss if available
      if (mappedHeaders.profitLoss) {
        try {
          const plStr = row[mappedHeaders.profitLoss]?.toString().replace(/[^0-9.-]+/g, '').trim();
          trade.profitLoss = plStr ? parseFloat(plStr) : undefined;
        } catch (e) {
          // Not essential, just log warning
          console.warn(`Could not parse P/L for row ${index + 1}: ${row[mappedHeaders.profitLoss]}`);
        }
      }
      
      trades.push(trade);
      
    } catch (e: any) {
      errorMessages[index] = `Row ${index + 1}: Unexpected error: ${e.message}`;
      console.error(`Error processing row ${index + 1}:`, e, "Row data:", row);
    }
  });

  // Check if we could process any trades
  if (trades.length === 0 && csvData.length > 0) {
    // Collect the first 5 error messages to show the user
    const errorList = Object.values(errorMessages).slice(0, 5).join('\n');
    
    throw new Error(
      "Could not process any rows from the CSV. Please check data format and ensure essential columns " +
      "(Date, Symbol, Action, Quantity, Price) are present and valid.\n\n" +
      (errorList ? `Sample errors:\n${errorList}` : "")
    );
  }

  console.log(`Mapped ${trades.length} trades from ${csvData.length} CSV rows.`);
  return trades;
};